import React, { useCallback, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classNames from 'classnames'
import EntityTextarea from 'react-entity-textarea'
import { MultiMenuTrigger } from 'react-multi-menu'
import { bindingTypes } from '@easybutton/constants'

import { buildInputBinding, getBindingLabel } from '../../../../utils/bindings'
import { humanize, capitalize } from '../../../../utils/strings'
import { getBindings } from '../../../../utils/recommender'
import { ADD_INPUT_MODAL } from '../../../../utils/modal-types'
import { showModal } from '../../../../ducks/modals'
import { getAction } from '../../../../ducks/actions'
import { IconButton } from '../../Icon'

import Entity from './Entity'

import styles from './DynamicInput.module.css'
import 'react-entity-textarea/styles.css'
import 'react-multi-menu/styles.css'
import './EntityTextarea.css'
import './MultiMenu.css'

export const BaseInput = function BaseInput({
  meta,
  label,
  input,
  className,
  options,
  getLabel,
  placeholder,
  monospace,
  newEntityCallback,
}) {
  const textarea = useRef(null)

  const renderEntity = useCallback((entity, opts) => (
    <Entity {...opts} entity={entity} getLabel={getLabel} />
  ))

  const { touched, error } = meta
  const { value, onChange } = input

  const handleChange = value => {
    onChange(value)
  }

  const handleAddEntity = async entity => {
    if (!textarea.current) return

    if (newEntityCallback) {
      let result = newEntityCallback(entity)

      entity = result && result.then ? await result : result
    }

    textarea.current.addEntity(entity)
  }

  return (
    <div
      className={classNames(styles.wrapper, className, {
        [styles.error]: touched && error,
      })}
    >
      {label && <label>{label}</label>}
      <div className={styles.innerWrapper}>
        <div
          className={classNames(
            styles.inputWrapper,
            'dynamic-input',
            monospace && 'dynamic-input-monospace'
          )}
        >
          <EntityTextarea
            value={value}
            onChange={handleChange}
            renderEntity={renderEntity}
            ref={textarea}
            placeholder={placeholder}
          />
        </div>
        <MultiMenuTrigger
          menu={options}
          className={styles.button}
          onSelect={handleAddEntity}
          rowHeight={40}
          width={300}
          childWidth={300}
        >
          <IconButton icon="lightning" color="blue" />
        </MultiMenuTrigger>
      </div>
      {touched && error ? (
        <div className={styles.errorMessage}>{error}</div>
      ) : null}
    </div>
  )
}

export default function ConnectedDynamicInput({
  actionId,
  stepId,
  databaseId,
  table,
  column,
  ...props
}) {
  const dispatch = useDispatch()
  const action = useSelector(getAction(actionId))

  const getLabel = useCallback(
    entity => {
      return getBindingLabel(entity, action)
    },
    [action]
  )

  const bindingOptions = useCallback(
    useSelector(getBindings(databaseId, table, column, actionId, stepId)),
    [action, stepId]
  )

  const handleAddEntity = async binding => {
    if (binding === 'addInput') {
      let result = await dispatch(
        showModal(ADD_INPUT_MODAL, {
          actionId,
          suggestedName: capitalize(humanize(props.label)),
        })
      )

      binding = buildInputBinding(result.value)
    } else if (
      binding &&
      binding.bindingType === bindingTypes.FUNCTION &&
      binding.arguments &&
      binding.arguments[0] === 'addInput'
    ) {
      let result = await dispatch(
        showModal(ADD_INPUT_MODAL, {
          actionId,
          suggestedName: capitalize(humanize(props.label)),
        })
      )

      let inputBinding = buildInputBinding(result.value)

      binding = {
        ...binding,
        arguments: [inputBinding],
      }
    }

    return binding
  }

  return (
    <BaseInput
      {...props}
      options={bindingOptions}
      getLabel={getLabel}
      newEntityCallback={handleAddEntity}
    />
  )
}
